import React, { useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import { useQuery, useNotify, useRefresh, useCreate } from 'react-admin';

/** Adds one lot (receipt line) to an open manifest. */
const AddLotDialog = ({ open, onClose, shipment }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [create, { loading }] = useCreate('supplyreceipts');

    const [supplyTypeId, setSupplyTypeId] = useState('');
    const [lotNo, setLotNo] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [qty, setQty] = useState('');
    const [calNor, setCalNor] = useState('');
    const [calLow, setCalLow] = useState('');
    const [calHigh, setCalHigh] = useState('');

    const { data: types } = useQuery({
        type: 'getList',
        resource: 'supplytypes',
        payload: {
            filter: { status: 1 },
            sort: { field: 'name', order: 'ASC' },
            pagination: { page: 1, perPage: 500 },
        },
    });

    const selected = (types || []).find(t => t.id === supplyTypeId);
    const isCalibration = selected && selected.category_id === 2;

    const reset = () => {
        setSupplyTypeId('');
        setLotNo('');
        setExpiryDate('');
        setQty('');
        setCalNor('');
        setCalLow('');
        setCalHigh('');
    };

    const handleSave = () => {
        if (!supplyTypeId || !lotNo || !expiryDate || !qty) {
            notify('Supply type, lot #, expiry date and qty are required', 'warning');
            return;
        }

        create(
            {
                payload: {
                    data: {
                        shipment_id: shipment.id,
                        supply_type_id: supplyTypeId,
                        lot_no: lotNo,
                        receipt_date: shipment.receipt_date,
                        expiry_date: expiryDate,
                        received_qty: Number(qty),
                        cal_nor: isCalibration && calNor !== '' ? Number(calNor) : null,
                        cal_low: isCalibration && calLow !== '' ? Number(calLow) : null,
                        cal_high: isCalibration && calHigh !== '' ? Number(calHigh) : null,
                    },
                },
            },
            {
                onSuccess: () => {
                    notify('Lot added');
                    reset();
                    onClose();
                    refresh();
                },
                onFailure: error => {
                    notify(
                        (error && error.body) || (error && error.message) || 'Could not add the lot',
                        'warning'
                    );
                },
            }
        );
    };

    if (!shipment) { return null; }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>Add Lot to Manifest {shipment.manifest_no}</DialogTitle>
            <DialogContent>
                <TextField
                    select fullWidth margin="dense" label="Supply Type" value={supplyTypeId}
                    onChange={e => setSupplyTypeId(e.target.value)}
                >
                    {(types || []).map(t => (
                        <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                    ))}
                </TextField>

                <TextField
                    fullWidth margin="dense" label="Lot #"
                    value={lotNo} onChange={e => setLotNo(e.target.value)}
                />

                <TextField
                    fullWidth margin="dense" type="date" label="Expiry Date"
                    InputLabelProps={{ shrink: true }}
                    value={expiryDate} onChange={e => setExpiryDate(e.target.value)}
                />

                <TextField
                    fullWidth margin="dense" type="number" label="Received Qty"
                    value={qty} onChange={e => setQty(e.target.value)}
                />

                {isCalibration && (
                    <React.Fragment>
                        <TextField
                            fullWidth margin="dense" type="number" label="Calibration Normal Value"
                            value={calNor} onChange={e => setCalNor(e.target.value)}
                        />
                        <TextField
                            fullWidth margin="dense" type="number" label="Calibration Low Value"
                            value={calLow} onChange={e => setCalLow(e.target.value)}
                        />
                        <TextField
                            fullWidth margin="dense" type="number" label="Calibration High Value"
                            value={calHigh} onChange={e => setCalHigh(e.target.value)}
                        />
                    </React.Fragment>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                <Button onClick={handleSave} color="primary" variant="contained" disabled={loading}>
                    Add
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddLotDialog;
